import React, { useState, useEffect } from 'react';
import { Delete, Plus, Trash2, Building2, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { financeApi } from '../../lib/api';
import { CAT_ICONS, CatIcon, curSym } from './pocketIcons';

const CURRENCIES = ['USD', 'ILS', 'JOD', 'EUR'];

// ── Quick expense keypad sheet ─────────────────────────────────────────
export const QuickAddSheet = ({ category, user, onClose, onSave }) => {
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState('amount');   // 'amount' | 'classify'
  const [count, setCount] = useState(5);

  const press = (k) => {
    if (k === 'del') { setAmount((a) => a.slice(0, -1)); return; }
    if (k === '.' && amount.includes('.')) return;
    if (amount.replace('.', '').length >= 8) return;
    setAmount((a) => (a === '' && k === '.' ? '0.' : a + k));
  };

  const goClassify = () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) { toast.error('أدخل مبلغًا صحيحًا'); return; }
    setCount(5);
    setStep('classify');
  };

  const commit = async (kind) => {   // kind: 'office' | 'personal'
    if (busy) return;
    setBusy(true);
    const val = parseFloat(amount);
    const payload = kind === 'personal'
      ? { type: 'personal', wallet: 'personal', person: (user?.name || user?.email?.split('@')[0] || 'شخصي'),
          amount: val, currency, category: category.name, description: '', source: 'pocket-quick' }
      : { type: 'office', wallet: 'reserve', amount: val, currency, category: category.name, description: '', source: 'pocket-quick' };
    await onSave(payload);
    setBusy(false);
  };

  // Countdown auto-picks "مكتب" (office) when it hits zero.
  useEffect(() => {
    if (step !== 'classify') return;
    if (count <= 0) { commit('office'); return; }
    const id = setTimeout(() => setCount((c) => c - 1), 1000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, count]);

  return (
    <>
      <div className="pk-backdrop" onClick={onClose} />
      <div className="pk-sheet" data-testid="pocket-quickadd-sheet">
        <div className="pk-grip" />
        <div className="pk-row" style={{ gap: 12 }}>
          <div className="pk-tx-ic" style={{ color: 'var(--pk-gold)', background: 'rgba(245,158,11,0.12)', borderColor: 'rgba(245,158,11,0.3)' }}><CatIcon icon={category.icon} size={19} /></div>
          <div>
            <div className="pk-sheet-title">{category.name}</div>
            <div className="pk-sheet-sub">{step === 'amount' ? 'مصروف سريع — أدخل المبلغ' : 'هذا المصروف — مكتب ولا شخصي؟'}</div>
          </div>
        </div>

        {step === 'amount' ? (
          <>
            <div className="pk-amount-display" dir="ltr" data-testid="pocket-quickadd-amount">
              {amount || <span className="ph">0</span>} <span className="cur">{curSym(currency)} {currency}</span>
            </div>
            <div className="pk-seg">
              {CURRENCIES.map((c) => (
                <button key={c} className={currency === c ? 'on' : ''} onClick={() => setCurrency(c)} data-testid={`pocket-cur-${c}`}>{curSym(c)} {c}</button>
              ))}
            </div>
            <div className="pk-keypad">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'del'].map((k) => (
                <button key={k} className="pk-key" onClick={() => press(k)} data-testid={`pocket-key-${k}`}>
                  {k === 'del' ? <Delete size={19} style={{ margin: '0 auto' }} /> : k}
                </button>
              ))}
            </div>
            <button className="pk-btn" onClick={goClassify} disabled={!amount} data-testid="pocket-quickadd-save">
              التالي — تصنيف المصروف
            </button>
          </>
        ) : (
          <div data-testid="pocket-classify-step">
            <div className="pk-amount-display" dir="ltr" style={{ fontSize: 30 }}>
              {amount} <span className="cur">{curSym(currency)} {currency}</span>
            </div>
            <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--pk-mut)', marginBottom: 12 }}>
              رح ينحفظ كـ <b>مكتب</b> تلقائيًا خلال <b dir="ltr">{count}</b> ثانية…
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="pk-btn mag" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center', padding: '16px 8px' }}
                onClick={() => commit('office')} disabled={busy} data-testid="pocket-classify-office">
                <Building2 size={22} /> مصروف مكتب
              </button>
              <button className="pk-btn" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center', padding: '16px 8px' }}
                onClick={() => commit('personal')} disabled={busy} data-testid="pocket-classify-personal">
                <UserRound size={22} /> مصروف شخصي
              </button>
            </div>
            <button className="pk-btn ghost" style={{ marginTop: 10 }} onClick={() => setStep('amount')} data-testid="pocket-classify-back">رجوع</button>
          </div>
        )}
      </div>
    </>
  );
};

// ── Manage categories sheet ────────────────────────────────────────────
export const ManageCatsSheet = ({ categories, onClose, onSaved }) => {
  const [cats, setCats] = useState(categories || []);
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('zap');
  const [busy, setBusy] = useState(false);

  const add = () => {
    const n = name.trim();
    if (!n) return;
    setCats((c) => [...c, { name: n, icon }]);
    setName(''); setIcon('zap');
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await financeApi.savePocketCategories(cats);
      onSaved(r.data.categories);
      toast.success('تم حفظ الفئات ✓');
      onClose();
    } catch {
      toast.error('فشل الحفظ');
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="pk-backdrop" onClick={onClose} />
      <div className="pk-sheet" data-testid="pocket-managecats-sheet">
        <div className="pk-grip" />
        <div className="pk-sheet-title">تخصيص الفئات السريعة</div>
        <div className="pk-sheet-sub">أضف أو احذف الاختصارات اللي بتناسبك</div>

        <div style={{ marginTop: 16 }}>
          {cats.map((c, i) => (
            <div key={i} className="pk-row" style={{ padding: '9px 4px', borderBottom: '1px solid var(--pk-line)' }}>
              <CatIcon icon={c.icon} size={18} style={{ color: 'var(--pk-cyan)' }} />
              <span style={{ flex: 1, fontSize: 14.5, fontWeight: 700 }}>{c.name}</span>
              <button className="pk-iconbtn" style={{ width: 34, height: 34 }} onClick={() => setCats((arr) => arr.filter((_, j) => j !== i))} data-testid={`pocket-cat-del-${i}`}>
                <Trash2 size={15} style={{ color: 'var(--pk-red)' }} />
              </button>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 16 }}>
          <div className="pk-row">
            <input className="pk-input" placeholder="اسم فئة جديدة…" value={name} onChange={(e) => setName(e.target.value)} data-testid="pocket-newcat-name" />
            <button className="pk-iconbtn on" style={{ flexShrink: 0 }} onClick={add} data-testid="pocket-newcat-add">
              <Plus size={18} />
            </button>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            {Object.keys(CAT_ICONS).map((k) => (
              <button key={k} className={`pk-iconbtn ${icon === k ? 'on' : ''}`} onClick={() => setIcon(k)} data-testid={`pocket-icon-${k}`}>
                <CatIcon icon={k} size={17} />
              </button>
            ))}
          </div>
        </div>

        <button className="pk-btn" onClick={save} disabled={busy} data-testid="pocket-managecats-save">حفظ التغييرات</button>
        <button className="pk-btn ghost" onClick={onClose}>إلغاء</button>
      </div>
    </>
  );
};
